import { createContext, ReactNode, useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { TransactionOriginatorType } from '@features/transactions/enums/transactionOriginatorType'
import { useBuildTransactionUrl } from '@features/transactions/hooks/useBuildTransactionUrl'
import { useGetTransactionPostings } from '@features/transactions/hooks/useGetTransactionPostings'
import { Posting } from '@features/transactions/types/posting'
import { SelectedTransaction } from '@features/transactions/types/selectedTransaction'
import { Transaction } from '@features/transactions/types/transaction'

import { useFetchTransaction } from '../../../hooks/useFetchTransaction'
import { useFetchTransactionOriginator } from '../../../hooks/useFetchTransactionOriginator'
import { OriginatorResult } from '../../../types/originatorResult'
import { getTransactionTypeFromOriginatorReference } from '../../../utils/getTransactionTypeFromOriginatorReference'
import { useFetchTransactionVoidPair } from '../hooks/useFetchTransactionVoidPair'

interface TransactionDrawerContextValue {
  transactionId: string
  transaction?: Transaction
  originatorResult: OriginatorResult
  postings: Posting[] | undefined
  isLoading: boolean
  isVoidedEntry: boolean
  journalLabel: string | null
  transactionNo: number | null
  creator: string | undefined
  originatorCurrencyId: string | null
  originatorName: string | null | undefined
  originatorType: TransactionOriginatorType | undefined
  navigateToTransaction: (transaction: SelectedTransaction) => void
  pairTransaction: Transaction | undefined
  translationKey: string | undefined
  url: string | undefined
}

const TransactionDrawerContext = createContext<TransactionDrawerContextValue | null>(null)

interface TransactionDrawerContextProviderProps {
  transactionId: string
  children: ReactNode
  onSelectTransaction: (transaction: SelectedTransaction) => void
}

export const TransactionDrawerContextProvider = ({
  transactionId,
  children,
  onSelectTransaction,
}: TransactionDrawerContextProviderProps) => {
  const { t } = useTranslation()
  const { transaction } = useFetchTransaction({ transactionId })
  const { getUrl } = useBuildTransactionUrl()

  const {
    amount,
    creator,
    isVoided,
    isVoid,
    journalCode,
    journalName,
    originatorCurrencyId,
    originatorName,
    originatorReference,
    transactionNo,
    side,
  } = transaction ?? {}

  const journalLabel = journalCode && journalName ? `${journalName} (${journalCode})` : null

  const { originatorId, originatorType, translationKey } =
    getTransactionTypeFromOriginatorReference(amount ?? null, originatorReference ?? null, side ?? null, t) ?? {}

  const originatorResult = useFetchTransactionOriginator({ originatorType, originatorId })
  const { postings, isLoading: isPostingsLoading } = useGetTransactionPostings(transactionId)

  const externalId =
    originatorResult.type === TransactionOriginatorType.ExternalInvoice
      ? originatorResult.originator?.externalId.toString()
      : undefined

  const url = getUrl({ externalId, originatorId, originatorType })

  const isVoidedEntry = Boolean(isVoided || isVoid)

  const { isLoading: isVoidPairLoading, pairTransaction } = useFetchTransactionVoidPair({
    isVoid,
    isVoided,
    originatorReference,
    originalPostings: postings,
    transactionId,
  })

  return (
    <TransactionDrawerContext.Provider
      value={{
        creator: creator?.name,
        isLoading: originatorResult.isLoading || isPostingsLoading || isVoidPairLoading,
        isVoidedEntry,
        journalLabel,
        originatorCurrencyId: originatorCurrencyId ?? null,
        originatorName,
        originatorResult,
        originatorType,
        pairTransaction,
        postings,
        transaction,
        transactionId,
        transactionNo: transactionNo ?? null,
        navigateToTransaction: onSelectTransaction,
        translationKey,
        url,
      }}
    >
      {children}
    </TransactionDrawerContext.Provider>
  )
}

export const useTransactionDrawerContext = (): TransactionDrawerContextValue => {
  const context = useContext(TransactionDrawerContext)

  if (!context) {
    throw new Error('useTransactionDrawerContext is missing in the module!')
  }

  return context
}
